import React, { useState, useRef, useEffect } from 'react'
import { X, MessageCircle, Send, Lock, AlertTriangle } from 'lucide-react'
import { ChatMessage, chatService, generateSessionId, formatMessageTime } from '@/services/chatService'
import analytics from '@/services/analyticsService'

interface ChatWidgetProps {
  agentName: string
  agentSubdomain: string
  agentAvatar?: string
  agentPlan?: string          // Plan del agente — controla si el chat está disponible
  autoOpen?: boolean
  propertyId?: string
  onChatOpen?: () => void
  onMessageSent?: (message: string, messageCount: number) => void
  onDataCaptured?: (data: any, score: number, temperature: string) => void
  onChatClose?: () => void
}

/**
 * PLANES SIN CHAT:
 * Si agentPlan está en PLANS_WITHOUT_CHAT, el widget retorna null completamente.
 * Sin botón, sin conexión al backend, sin errores.
 *
 * Para agregar un plan futuro sin chat, solo agregar su id a este array.
 * No tocar ningún template ni lógica interna.
 *
 * Ver: HANDOFF_TEMPLATES_PLANES.md para guía completa.
 */
const PLANS_WITHOUT_CHAT: string[] = ['starter']

/**
 * Guard de plan — wrapper público del ChatWidget.
 * Los templates usan este componente directamente.
 * ChatWidgetInner contiene toda la lógica real.
 */
const ChatWidget: React.FC<ChatWidgetProps> = (props) => {
  const planNormalized = props.agentPlan?.toLowerCase() || ''
  if (PLANS_WITHOUT_CHAT.includes(planNormalized)) return null
  return <ChatWidgetInner {...props} />
}

export default ChatWidget

// ChatWidgetInner — lógica completa del chat

const ChatWidgetInner: React.FC<ChatWidgetProps> = ({

  agentName,
  agentSubdomain,
  agentAvatar,
  agentPlan,
  autoOpen = true,
  propertyId,
  onChatOpen,
  onMessageSent,
  onDataCaptured,
  onChatClose,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sessionId] = useState(() => generateSessionId())
  const [isClosing, setIsClosing] = useState(false)
  const [messageCount, setMessageCount] = useState(0)
  const [hasAutoOpened, setHasAutoOpened] = useState(false)
  const [isChatClosed, setIsChatClosed] = useState(false)
  const [closingMessage, setClosingMessage] = useState('')
  const [isPreClose, setIsPreClose] = useState(false)
  const [showTooltip, setShowTooltip] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const autoOpenTimeoutRef = useRef<NodeJS.Timeout>()
  const inactivityTimeoutRef = useRef<NodeJS.Timeout>()
  const preCloseTimeoutRef = useRef<NodeJS.Timeout>()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (!isLoading && isOpen && !isChatClosed) {
      inputRef.current?.focus()
    }
  }, [isLoading, isOpen, isChatClosed])

  useEffect(() => {
    if (autoOpen && !hasAutoOpened) {
      autoOpenTimeoutRef.current = setTimeout(() => {
        handleOpenChat()
        setHasAutoOpened(true)
      }, 3000)
    }
    return () => {
      if (autoOpenTimeoutRef.current) clearTimeout(autoOpenTimeoutRef.current)
    }
  }, [autoOpen, hasAutoOpened])

  // Inactivity timeout - 5 minutes (general)
  useEffect(() => {
    if (isOpen && !isChatClosed && messages.length > 1 && !isPreClose) {
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current)

      inactivityTimeoutRef.current = setTimeout(() => {
        console.log('⏰ Inactivity timeout (5 min) - closing chat')
        handleInactivityClose()
      }, 300000) // 5 minutes
    }

    return () => {
      if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current)
    }
  }, [messages, isOpen, isChatClosed, isPreClose])

  // Pre-close timeout - 60 seconds
  useEffect(() => {
    if (isPreClose && !isChatClosed) {
      if (preCloseTimeoutRef.current) clearTimeout(preCloseTimeoutRef.current)

      preCloseTimeoutRef.current = setTimeout(() => {
        console.log('⏰ Pre-close timeout (60 sec) - finalizing chat')
        handlePreCloseTimeout()
      }, 60000) // 60 seconds
    }

    return () => {
      if (preCloseTimeoutRef.current) clearTimeout(preCloseTimeoutRef.current)
    }
  }, [isPreClose, isChatClosed, messages])

  const handleInactivityClose = () => {
    setIsChatClosed(true)
    setClosingMessage('Esta conversación ha finalizado por inactividad. Recarga la página para iniciar una nueva.\n\nThis conversation has ended due to inactivity. Reload the page to start a new one.')
    chatService.closeSession(agentSubdomain, sessionId, 'inactivity_timeout').catch(console.error)
    
    // Analytics tracking
    analytics.trackChatEvent('chat_closed', { 
      messageCount, 
      closeReason: 'inactivity_timeout' 
    })
  }

  const handlePreCloseTimeout = () => {
    setIsChatClosed(true)
    setClosingMessage('¡Gracias! En breve nos pondremos en contacto contigo.\n\nThank you! We will contact you shortly.')
    chatService.closeSession(agentSubdomain, sessionId, 'pre_close_timeout').catch(console.error)
    
    // Analytics tracking
    analytics.trackChatEvent('chat_closed', { 
      messageCount, 
      closeReason: 'pre_close_timeout' 
    })
  }

  const handleOpenChat = () => {
    setIsOpen(true)
    setShowTooltip(false)
    if (messages.length === 0) {
      // Mensaje bilingüe de bienvenida
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        role: 'assistant',
        content: `¡Hola! / Hello!\n\nSoy ${agentName}, tu asesor inmobiliario.\nI'm ${agentName}, your real estate advisor.\n\n¿En qué idioma prefieres continuar?\nWhat language do you prefer?\n\n🇲🇽 Español  |  🇺🇸 English`,
        timestamp: new Date(),
      }
      setMessages([welcomeMessage])
    }
    onChatOpen?.()
    
    // Analytics tracking
    analytics.trackChatEvent('chat_opened')
    
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const handleCloseChat = async () => {
    setIsClosing(true)
    try {
      await chatService.closeSession(agentSubdomain, sessionId, 'user_closed')
      onChatClose?.()
      
      // Analytics tracking
      analytics.trackChatEvent('chat_closed', { 
        messageCount, 
        closeReason: 'user_closed' 
      })
    } catch (error) {
      console.error('Error closing session:', error)
    }
    setIsClosing(false)
    setShowTooltip(true)
    setIsOpen(false)
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || isChatClosed) return

    // Clear timeouts on user activity
    if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current)
    if (preCloseTimeoutRef.current) clearTimeout(preCloseTimeoutRef.current)

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)

    const currentCount = messageCount + 1
    setMessageCount(currentCount)
    onMessageSent?.(inputValue, currentCount)
    
    // Analytics tracking - message sent
    analytics.trackChatEvent('message_sent', { 
      userMessage: inputValue, 
      messageCount: currentCount 
    })

    try {
      const response = await chatService.sendMessage(agentSubdomain, inputValue, sessionId, propertyId)

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, assistantMessage])

      if (response.leadData) {
        const temperature = response.score >= 80 ? 'hot' : response.score >= 60 ? 'warm' : 'cold'
        onDataCaptured?.(response.leadData, response.score, temperature)
        
        // Analytics tracking - data captured
        analytics.trackChatEvent('data_captured', { 
          dataCaptured: response.leadData, 
          leadScore: response.score, 
          leadTemperature: temperature 
        })
      }

      // Manejo de estados
      if (response.closeReason === 'pre_close') {
        console.log('🔔 Entering pre-close state')
        setIsPreClose(true)
      }

      if (response.chatClosed) {
        console.log('🔒 Chat closed:', response.closeReason)
        setIsChatClosed(true)
        setIsPreClose(false)

        // Analytics tracking - chat closed by system
        analytics.trackChatEvent('chat_closed', { 
          messageCount: currentCount, 
          closeReason: response.closeReason 
        })

        // Mensaje según razón de cierre
        if (response.closeReason === 'user_confirmed_close' || response.closeReason === 'lead_qualified') {
          setClosingMessage('¡Gracias! En breve nos pondremos en contacto contigo.\n\nThank you! We will contact you shortly.')
        } else if (response.closeReason === 'max_messages_reached') {
          setClosingMessage('Conversación finalizada. ¡Gracias por tu interés!\n\nConversation ended. Thanks for your interest!')
        } else {
          setClosingMessage('Esta conversación ha finalizado. Recarga la página para iniciar una nueva.\n\nThis conversation has ended. Reload the page to start a new one.')
        }
      }

    } catch (error) {
      console.error('Error:', error)
      setMessages((prev) => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Lo siento, hubo un problema. ¿Puedes intentar de nuevo?\n\nSorry, there was a problem. Can you try again?',
        timestamp: new Date(),
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 flex items-center space-x-3">
        {showTooltip && (
          <div
            className="bg-white text-gray-700 px-4 py-2.5 rounded-2xl shadow-lg text-sm font-medium cursor-pointer border border-gray-100 hover:shadow-xl transition-shadow"
            onClick={handleOpenChat}
          >
            ¿En qué puedo ayudarte?
          </div>
        )}
        <button
          onClick={handleOpenChat}
          className="relative w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 overflow-hidden border-2 border-white"
          aria-label="Abrir chat"
        >
          {agentAvatar ? (
            <img src={agentAvatar} alt={agentName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white text-lg font-bold">
              {agentName.charAt(0)}
            </div>
          )}
          <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></span>
        </button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 w-[calc(100vw-2rem)] sm:w-96 h-[500px] sm:h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200 max-w-[400px]">
      <div className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {agentAvatar ? (
            <img src={agentAvatar} alt={agentName} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-lg font-bold">
              {agentName.charAt(0)}
            </div>
          )}
          <div>
            <h3 className="font-semibold">{agentName}</h3>
            <p className="text-xs opacity-90">
              {isChatClosed ? 'Finalizado / Ended' : isPreClose ? 'Finalizando... / Ending...' : 'En línea / Online'}
            </p>
          </div>
        </div>
        <button onClick={handleCloseChat} disabled={isClosing} className="hover:bg-blue-700 p-1 rounded disabled:opacity-50">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}>
            {msg.role === 'system' ? (
              <div className="bg-yellow-50 border border-yellow-200 px-4 py-3 rounded-lg max-w-[90%] flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 whitespace-pre-line">{msg.content}</p>
                  <p className="text-xs text-yellow-600 mt-1">{formatMessageTime(msg.timestamp)}</p>
                </div>
              </div>
            ) : (
              <div className={`max-w-[80%] px-4 py-2 rounded-lg ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border border-gray-200'}`}>
                <p className="text-sm whitespace-pre-line">{msg.content}</p>
                <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>{formatMessageTime(msg.timestamp)}</p>
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 px-4 py-2 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
        {isChatClosed ? (
          <div className="flex flex-col items-center justify-center space-y-2 text-gray-500 py-2">
            <Lock className="w-5 h-5" />
            <p className="text-sm text-center whitespace-pre-line">{closingMessage}</p>
          </div>
        ) : (
          <div className="flex space-x-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje... / Type your message..."
              disabled={isLoading}
              autoFocus
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// export movido al wrapper ChatWidget arriba
